/**
 * Settings → Users & Roles (Step 3 of in-app user management).
 *
 * Three tabs:
 *   1. Members             — DataTable with filters, bulk actions, export.
 *   2. Pending Invitations — list + revoke.
 *   3. Roles & Permissions — the M21 role-reference table (unchanged).
 *
 * Modals (in this file, kept inline for review): Invite, Change Role,
 * Remove, Suspend. They use react-query mutations and update the
 * cached lists via setQueryData / invalidateQueries.
 *
 * Permission model is server-enforced (api/routes/settings_users.py):
 *   - require_role >= hr_director on every endpoint.
 *   - tenant_admin grant requires actor IS tenant_admin.
 *   - Self-protection: server refuses self-mutations with 400.
 *   - Last-admin protection: server refuses removing/demoting/
 *     suspending the only remaining tenant_admin.
 * The UI mirrors these guards by hiding / disabling buttons when
 * obviously inapplicable, but the source of truth is the server.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import {
  AlertTriangle,
  Eye,
  Mail,
  MoreHorizontal,
  Pause,
  Play,
  ShieldCheck,
  Trash2,
  UserCog,
  UserPlus,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useUser } from "@clerk/clerk-react";

import {
  DataTable,
  EmptyState,
  EntityDrawer,
  PageHeader,
} from "../../components/ui";
import { api } from "../../lib/api";
import {
  TENANT_ROLES,
  type TenantInvitation,
  type TenantMember,
  type TenantRole,
} from "../../lib/api";

type Tab = "members" | "invitations" | "roles";

const _ROLE_LABEL_KEYS: Record<TenantRole, string> = {
  tenant_admin: "app.ur_role_tenant_admin",
  hr_director: "app.ur_role_hr_director",
  hr_partner: "app.ur_role_hr_partner",
  manager: "app.ur_role_manager",
  employee: "app.ur_role_employee",
};

const _ROLE_BADGE_CLASS: Record<TenantRole, string> = {
  tenant_admin:
    "bg-brand-blue/10 text-brand-blue border-brand-blue/20",
  hr_director:
    "bg-brand-purple/10 text-brand-purple border-brand-purple/20",
  hr_partner:
    "bg-brand-teal/10 text-brand-teal border-brand-teal/20",
  manager: "bg-amber-100 text-amber-900 border-amber-200",
  employee: "bg-surface-sunken text-text-secondary border-border-subtle",
};

function RoleBadge({ role }: { role: TenantRole }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${_ROLE_BADGE_CLASS[role]}`}
    >
      {t(_ROLE_LABEL_KEYS[role])}
    </span>
  );
}

function StatusBadge({ status }: { status: "active" | "suspended" }) {
  const { t } = useTranslation();
  if (status === "suspended") {
    return (
      <span className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
        <Pause size={10} /> {t("app.ur_status_suspended")}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
      {t("app.ur_status_active")}
    </span>
  );
}

function useRelFormatter() {
  const { t } = useTranslation();
  return function fmtRelative(ms: number | null): string {
    if (!ms) return "—";
    const diff = Date.now() - ms;
    const min = Math.round(diff / 60_000);
    if (min < 1) return t("app.ur_rel_just_now");
    if (min < 60) return t("app.ur_rel_minutes", { n: min });
    const hr = Math.round(min / 60);
    if (hr < 24) return t("app.ur_rel_hours", { n: hr });
    const day = Math.round(hr / 24);
    if (day < 30) return t("app.ur_rel_days", { n: day });
    const mo = Math.round(day / 30);
    if (mo < 12) return t("app.ur_rel_months", { n: mo });
    return t("app.ur_rel_years", { n: Math.round(mo / 12) });
  };
}

// ============================================================================
// Page
// ============================================================================

export function UsersRolesPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("members");
  const [inviteOpen, setInviteOpen] = useState(false);

  // Pending invitation count for the tab pill — cheap query, cached.
  const invitationsQuery = useQuery({
    queryKey: ["tenant-invitations"],
    queryFn: () => api.listTenantInvitations(),
  });
  const pendingCount = invitationsQuery.data?.length ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={t("app.ur_title")}
        subtitle={t("app.ur_subtitle")}
        breadcrumbs={[
          { label: t("app.settings_breadcrumb"), href: "/settings" },
          { label: t("app.ur_breadcrumb") },
        ]}
        actions={
          <button
            type="button"
            onClick={() => setInviteOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90"
          >
            <UserPlus size={14} /> {t("app.ur_invite_member")}
          </button>
        }
      />

      <nav
        aria-label={t("app.ur_aria_sections")}
        role="tablist"
        className="flex items-center gap-1 border-b border-border-subtle"
      >
        <TabButton active={tab === "members"} onClick={() => setTab("members")}>
          {t("app.ur_tab_members")}
        </TabButton>
        <TabButton
          active={tab === "invitations"}
          onClick={() => setTab("invitations")}
        >
          {t("app.ur_tab_invitations")}
          {pendingCount > 0 && (
            <span className="ms-1.5 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-900">
              {pendingCount}
            </span>
          )}
        </TabButton>
        <TabButton active={tab === "roles"} onClick={() => setTab("roles")}>
          {t("app.ur_tab_roles")}
        </TabButton>
      </nav>

      {tab === "members" && <MembersTab />}
      {tab === "invitations" && (
        <InvitationsTab invitations={invitationsQuery.data ?? []} />
      )}
      {tab === "roles" && <RolesReferenceTab />}

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={[
        "inline-flex items-center px-3 py-2 -mb-px border-b-2 text-sm font-medium transition-colors",
        active
          ? "border-brand-blue text-brand-blue"
          : "border-transparent text-text-secondary hover:text-text-primary",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

// ============================================================================
// Members tab — DataTable
// ============================================================================

function MembersTab() {
  const { t } = useTranslation();
  const fmtRelative = useRelFormatter();
  const qc = useQueryClient();
  const { user: clerkUser } = useUser();
  const me = clerkUser?.id ?? null;

  const q = useQuery({
    queryKey: ["tenant-members"],
    queryFn: () => api.listTenantMembers(),
    refetchOnMount: "always",
  });

  const [roleFilter, setRoleFilter] = useState<TenantRole | "all">("all");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "suspended"
  >("all");

  const [actionTarget, setActionTarget] = useState<
    | { kind: "change-role"; member: TenantMember }
    | { kind: "remove"; member: TenantMember }
    | { kind: "suspend"; member: TenantMember }
    | { kind: "unsuspend"; member: TenantMember }
    | null
  >(null);

  const rows = useMemo(() => {
    const all = q.data ?? [];
    return all.filter((m) => {
      if (roleFilter !== "all" && m.role !== roleFilter) return false;
      if (statusFilter !== "all" && m.status !== statusFilter) return false;
      return true;
    });
  }, [q.data, roleFilter, statusFilter]);

  const columns = useMemo<ColumnDef<TenantMember, unknown>[]>(
    () => [
      {
        id: "name",
        header: t("app.ur_col_name"),
        accessorFn: (m) => m.full_name,
        cell: ({ row }) => {
          const m = row.original;
          return (
            <div className="flex items-center gap-2">
              {m.image_url ? (
                <img
                  src={m.image_url}
                  alt=""
                  className="h-7 w-7 rounded-full object-cover"
                />
              ) : (
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-gradient text-[10px] font-semibold text-white">
                  {(m.first_name?.[0] || m.email?.[0] || "?").toUpperCase()}
                </span>
              )}
              <Link
                to={`/settings/users/${encodeURIComponent(m.user_id)}`}
                className="text-sm font-medium text-text-primary hover:underline"
              >
                {m.full_name}
              </Link>
            </div>
          );
        },
      },
      {
        id: "email",
        header: t("app.ur_col_email"),
        accessorFn: (m) => m.email ?? "—",
      },
      {
        id: "role",
        header: t("app.ur_col_role"),
        accessorFn: (m) => m.role,
        cell: ({ row }) => <RoleBadge role={row.original.role} />,
      },
      {
        id: "status",
        header: t("app.ur_col_status"),
        accessorFn: (m) => m.status,
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: "last_active",
        header: t("app.ur_col_last_active"),
        accessorFn: (m) => fmtRelative(m.last_active_at_ms),
      },
      {
        id: "joined",
        header: t("app.ur_col_joined"),
        accessorFn: (m) => fmtRelative(m.joined_at_ms),
      },
      {
        id: "actions",
        header: () => (
          <span className="sr-only">{t("app.ur_row_actions")}</span>
        ),
        enableSorting: false,
        cell: ({ row }) => {
          const m = row.original;
          const isSelf = me === m.user_id;
          return (
            <RowActions
              isSelf={isSelf}
              member={m}
              onAction={(kind) => setActionTarget({ kind, member: m })}
            />
          );
        },
      },
    ],
    [me, t, fmtRelative],
  );

  return (
    <div className="flex flex-col gap-3">
      {/* Filters --------------------------------------------------------- */}
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1 text-xs text-text-secondary">
          {t("app.ur_filter_role")}
          <select
            value={roleFilter}
            onChange={(e) =>
              setRoleFilter(e.target.value as TenantRole | "all")
            }
            className="rounded-md border border-border-subtle bg-surface-default px-2 py-1 text-xs text-text-primary"
          >
            <option value="all">{t("app.ur_filter_all_roles")}</option>
            {TENANT_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(_ROLE_LABEL_KEYS[r])}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1 text-xs text-text-secondary">
          {t("app.ur_filter_status")}
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(
                e.target.value as "all" | "active" | "suspended",
              )
            }
            className="rounded-md border border-border-subtle bg-surface-default px-2 py-1 text-xs text-text-primary"
          >
            <option value="all">{t("app.ur_status_all")}</option>
            <option value="active">{t("app.ur_status_active")}</option>
            <option value="suspended">{t("app.ur_status_suspended")}</option>
          </select>
        </label>
      </div>

      <DataTable
        columns={columns}
        data={rows}
        loading={q.isLoading}
        exportFilename="solvait-members"
        emptyState={
          <EmptyState
            icon={UserPlus}
            headline="No members match"
            description={
              q.data && q.data.length === 0
                ? "You're the only member of this tenant. Invite your team to get started."
                : "Try clearing your filters."
            }
          />
        }
      />

      {actionTarget?.kind === "change-role" && (
        <ChangeRoleModal
          member={actionTarget.member}
          onClose={() => setActionTarget(null)}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ["tenant-members"] });
            setActionTarget(null);
          }}
        />
      )}
      {actionTarget?.kind === "remove" && (
        <RemoveConfirmModal
          member={actionTarget.member}
          onClose={() => setActionTarget(null)}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ["tenant-members"] });
            setActionTarget(null);
          }}
        />
      )}
      {actionTarget?.kind === "suspend" && (
        <SuspendModal
          member={actionTarget.member}
          onClose={() => setActionTarget(null)}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ["tenant-members"] });
            setActionTarget(null);
          }}
        />
      )}
      {actionTarget?.kind === "unsuspend" && (
        <UnsuspendConfirm
          member={actionTarget.member}
          onClose={() => setActionTarget(null)}
          onDone={() => {
            void qc.invalidateQueries({ queryKey: ["tenant-members"] });
            setActionTarget(null);
          }}
        />
      )}
    </div>
  );
}

function RowActions({
  isSelf,
  member,
  onAction,
}: {
  isSelf: boolean;
  member: TenantMember;
  onAction: (
    kind: "change-role" | "remove" | "suspend" | "unsuspend",
  ) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative inline-block text-end" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-label={t("app.ur_row_actions")}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-text-secondary hover:bg-surface-sunken"
      >
        <MoreHorizontal size={14} />
      </button>
      {open && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <ul
            role="menu"
            className="absolute end-0 z-20 mt-1 w-44 overflow-hidden rounded-md border border-border-subtle bg-surface-default text-sm shadow-md"
          >
            <li>
              <Link
                to={`/settings/users/${encodeURIComponent(member.user_id)}`}
                className="flex items-center gap-2 px-3 py-2 text-text-primary hover:bg-surface-sunken"
                onClick={() => setOpen(false)}
                role="menuitem"
              >
                <Eye size={12} /> View profile
              </Link>
            </li>
            <li>
              <button
                type="button"
                role="menuitem"
                disabled={isSelf}
                onClick={() => {
                  onAction("change-role");
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-text-primary hover:bg-surface-sunken disabled:cursor-not-allowed disabled:text-text-tertiary"
                title={
                  isSelf
                    ? "You can't change your own role."
                    : undefined
                }
              >
                <UserCog size={12} /> Change role
              </button>
            </li>
            <li>
              {member.status === "active" ? (
                <button
                  type="button"
                  role="menuitem"
                  disabled={isSelf}
                  onClick={() => {
                    onAction("suspend");
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-amber-700 hover:bg-surface-sunken disabled:cursor-not-allowed disabled:text-text-tertiary"
                  title={
                    isSelf ? "You can't suspend yourself." : undefined
                  }
                >
                  <Pause size={12} /> Suspend
                </button>
              ) : (
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    onAction("unsuspend");
                    setOpen(false);
                  }}
                  className="flex w-full items-center gap-2 px-3 py-2 text-emerald-700 hover:bg-surface-sunken"
                >
                  <Play size={12} /> Unsuspend
                </button>
              )}
            </li>
            <li>
              <button
                type="button"
                role="menuitem"
                disabled={isSelf}
                onClick={() => {
                  onAction("remove");
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-rose-700 hover:bg-surface-sunken disabled:cursor-not-allowed disabled:text-text-tertiary"
                title={isSelf ? "You can't remove yourself." : undefined}
              >
                <Trash2 size={12} /> Remove
              </button>
            </li>
          </ul>
        </>
      )}
    </div>
  );
}

// ============================================================================
// Invitations tab
// ============================================================================

function InvitationsTab({ invitations }: { invitations: TenantInvitation[] }) {
  const { t } = useTranslation();
  const fmtRelative = useRelFormatter();
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: (id: string) => api.revokeTenantInvitation(id),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["tenant-invitations"] }),
  });

  if (invitations.length === 0) {
    return (
      <EmptyState
        icon={Mail}
        headline={t("operations.approvals_empty_headline")}
        description={t("app.ur_inv_empty_body")}
      />
    );
  }

  return (
    <ul className="divide-y divide-border-subtle overflow-hidden rounded-md border border-border-subtle">
      {invitations.map((inv) => (
        <li
          key={inv.invitation_id}
          className="flex flex-wrap items-center justify-between gap-3 px-4 py-3"
        >
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
              <Mail size={12} className="text-text-secondary" />
              {inv.email}
              <RoleBadge role={inv.role} />
            </div>
            <p className="text-xs text-text-tertiary">
              Sent {fmtRelative(inv.created_at_ms)}. Status:{" "}
              <span className="font-medium text-text-secondary">
                {inv.status}
              </span>
              .
            </p>
          </div>
          <button
            type="button"
            onClick={() => revoke.mutate(inv.invitation_id)}
            disabled={revoke.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-border-subtle px-3 py-1.5 text-xs text-text-secondary hover:bg-surface-sunken disabled:opacity-60"
          >
            <X size={12} /> Revoke
          </button>
        </li>
      ))}
    </ul>
  );
}

// ============================================================================
// Roles & Permissions reference (unchanged from M21)
// ============================================================================

const _ROLE_REFERENCE: { name: string; desc: string }[] = [
  {
    name: "Tenant Admin",
    desc: "Full control over tenant settings, legal entities, billing, integrations, and role assignments. The only role that can grant other Tenant Admins.",
  },
  {
    name: "HR Director",
    desc: "Manages members + policies + workflows. Can invite, change role (except Tenant Admin), remove, suspend.",
  },
  {
    name: "HR Partner",
    desc: "Day-to-day HR operations on assigned scope: leave / loans / EOS / attendance.",
  },
  {
    name: "Manager",
    desc: "Approves requests routed to them; reads their team.",
  },
  {
    name: "Employee",
    desc: "Self-service via the Concierge — own profile + requests.",
  },
];

function RolesReferenceTab() {
  return (
    <div className="overflow-hidden rounded-xl border border-border-subtle bg-surface-default">
      <header className="flex items-center gap-2 border-b border-border-subtle px-4 py-3">
        <ShieldCheck size={15} className="text-text-secondary" />
        <h2 className="text-sm font-semibold text-text-primary">
          Role reference
        </h2>
      </header>
      <ul className="divide-y divide-border-subtle">
        {_ROLE_REFERENCE.map((r) => (
          <li key={r.name} className="px-4 py-3">
            <div className="text-sm font-medium text-text-primary">
              {r.name}
            </div>
            <div className="text-xs text-text-secondary">{r.desc}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================================================================
// Modals — Invite / Change Role / Remove / Suspend / Unsuspend
// ============================================================================

// Modals on this page use <EntityDrawer /> — the canonical popup
// pattern (slides in from the trailing edge with a darkened
// backdrop). The drawer's footer slot is where action buttons
// live; children carry the form body.

function InviteModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TenantRole>("employee");
  const [err, setErr] = useState<string | null>(null);

  const m = useMutation({
    mutationFn: () => api.inviteTenantMember({ email, role }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["tenant-invitations"] });
      onClose();
      setEmail("");
      setRole("employee");
      setErr(null);
    },
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <EntityDrawer
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("app.ur_invite_modal_title")}
      description={t("app.ur_invite_desc")}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            form="invite-member-form"
            disabled={m.isPending || !email}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-60"
          >
            {m.isPending ? t("app.ur_sending") : t("app.ur_send_invitation")}
          </button>
        </div>
      }
    >
      <form
        id="invite-member-form"
        onSubmit={(e) => {
          e.preventDefault();
          setErr(null);
          m.mutate();
        }}
        className="flex flex-col gap-3"
      >
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">
            {t("app.ur_work_email")}
          </span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("app.ur_email_placeholder")}
            className="rounded-md border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
          />
        </label>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">{t("app.ur_role_label")}</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as TenantRole)}
            className="rounded-md border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
          >
            {TENANT_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(_ROLE_LABEL_KEYS[r])}
              </option>
            ))}
          </select>
          <span className="text-xs text-text-tertiary">
            {t("app.ur_invite_hint")}
          </span>
        </label>
        {err && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
          >
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {err}
          </div>
        )}
      </form>
    </EntityDrawer>
  );
}

function ChangeRoleModal({
  member,
  onClose,
  onDone,
}: {
  member: TenantMember;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [role, setRole] = useState<TenantRole>(member.role);
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.changeTenantMemberRole(member.user_id, role),
    onSuccess: onDone,
    onError: (e: Error) => setErr(e.message),
  });

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("app.ur_change_role_title", { name: member.full_name })}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={m.isPending || role === member.role}
            onClick={() => {
              setErr(null);
              m.mutate();
            }}
            className="rounded-md bg-brand px-3 py-1.5 text-sm font-medium text-brand-fg hover:opacity-90 disabled:opacity-60"
          >
            {m.isPending ? t("app.ur_saving") : t("app.ur_change_role_btn")}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          {t("app.ur_current_role_prefix")} <RoleBadge role={member.role} />
        </p>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">{t("app.ur_new_role_label")}</span>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as TenantRole)}
            className="rounded-md border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
          >
            {TENANT_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(_ROLE_LABEL_KEYS[r])}
              </option>
            ))}
          </select>
          <span className="text-xs text-text-tertiary">
            {t("app.ur_change_role_hint")}
          </span>
        </label>
        {err && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
          >
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {err}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

function RemoveConfirmModal({
  member,
  onClose,
  onDone,
}: {
  member: TenantMember;
  onClose: () => void;
  onDone: () => void;
}) {
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.removeTenantMember(member.user_id),
    onSuccess: onDone,
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={`Remove ${member.full_name}?`}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={m.isPending}
            onClick={() => {
              setErr(null);
              m.mutate();
            }}
            className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-60"
          >
            {m.isPending ? "Removing…" : "Remove member"}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          {member.full_name} ({member.email}) will be removed from this
          tenant. Their account in other tenants is unaffected, and
          their HR data history in this tenant is preserved.
        </p>
        {err && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
          >
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {err}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

function SuspendModal({
  member,
  onClose,
  onDone,
}: {
  member: TenantMember;
  onClose: () => void;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () =>
      api.suspendTenantMember(member.user_id, reason || undefined),
    onSuccess: onDone,
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t("app.ur_suspend_title", { name: member.full_name })}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            disabled={m.isPending}
            onClick={() => {
              setErr(null);
              m.mutate();
            }}
            className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-60"
          >
            {m.isPending ? t("app.ur_suspending") : t("app.ur_suspend_btn")}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          {t("app.ur_suspend_body")}
        </p>
        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">
            {t("app.ur_reason_optional")}
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder={t("app.ur_reason_placeholder")}
            className="rounded-md border border-border-subtle bg-surface-default px-3 py-2 text-sm text-text-primary"
          />
        </label>
        {err && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
          >
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {err}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

function UnsuspendConfirm({
  member,
  onClose,
  onDone,
}: {
  member: TenantMember;
  onClose: () => void;
  onDone: () => void;
}) {
  const [err, setErr] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: () => api.unsuspendTenantMember(member.user_id),
    onSuccess: onDone,
    onError: (e: Error) => setErr(e.message),
  });
  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={`Unsuspend ${member.full_name}?`}
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border-subtle px-3 py-1.5 text-sm text-text-secondary hover:bg-surface-sunken"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={m.isPending}
            onClick={() => {
              setErr(null);
              m.mutate();
            }}
            className="rounded-md bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {m.isPending ? "Restoring…" : "Unsuspend"}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-sm text-text-secondary">
          Restore {member.full_name}'s ability to make changes in this
          tenant.
        </p>
        {err && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700"
          >
            <AlertTriangle size={12} className="mt-0.5 shrink-0" />
            {err}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}
